import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top indicating scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", update);
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="p-6 bg-white shadow-sm border border-[var(--color-secondary-muted)] w-full"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-sm text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const Tokenomics = () => (
  <div className="relative overflow-hidden space-y-10 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Introduction */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-6xl mx-auto space-y-4 text-center"
    >
      <Paragraph className="text-gray-700 max-w-6xl mx-auto text-base sm:text-lg md:text-lg">
        Uncensored Waves (UcWaves) structures its token economy to balance
        community distribution, liquidity, staking incentives, team alignment,
        and long-term sustainability. Here’s a breakdown across our 1B NAU
        supply and its extensions (NAUY & NAUN).
      </Paragraph>
    </motion.div>

    {/* Two-column layout */}
    <div className="flex flex-col md:flex-row gap-8 max-w-6xl mx-auto">
      {/* Right column: 1, 2, 3 */}
      <SectionCard title=" ">
        <div className="flex flex-col gap-6">
          <div>
            <h4 className="text-blue-900 font-semibold mb-2">
              1. Fixed Supply & Mint
            </h4>
            <ul className="list-disc list-inside text-sm text-gray-800">
              <li>
                <strong>NAU:</strong> 1,000,000,000 minted at genesis. No future
                minting allowed.
              </li>
              <li>
                <strong>NAUY/NAUN:</strong> 15M each minted initially. Future
                minting occurs only by burning NAU at predefined ratios.
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-blue-900 font-semibold mb-2">
              2. Launchpad Distribution
            </h4>
            <ul className="list-disc list-inside text-sm text-gray-800">
              <li>125,000,000 NAU allocated to the presale.</li>
              <li>
                Price: 1 NAU = 0.0024 USDC. This represents a discount of 15% to
                the initial Uniswap Liquidity Pool listing price.
              </li>
            </ul>
            <Paragraph>
              This round supports initial community formation and base
              liquidity.
            </Paragraph>
          </div>

          <div>
            <h4 className="text-blue-900 font-semibold mb-2">
              3. Staking Incentive Funding
            </h4>
            <ul className="list-disc list-inside text-sm text-gray-800">
              <li>350M NAU sent to staking controller wallet.</li>
              <li>5M NAUY + 5M NAUN sent to staking controller wallet.</li>
              <li>
                As NAU is burned, NAUY/NAUN are minted in the proper proportions
                for rewards.
              </li>
            </ul>
            <Paragraph>
              Reward rates are static until governance transitions to DAO.
            </Paragraph>
          </div>
        </div>
      </SectionCard>

      {/* Left column: 4, 5, 6 */}
      <SectionCard title=" ">
        <div className="flex flex-col gap-6">
          <div>
            <h4 className="text-blue-900 font-semibold mb-2">
              4. Uniswap Liquidity Pools
            </h4>
            <ul className="list-disc list-inside text-sm text-gray-800">
              <li>
                NAU/USDC: 75M NAU + 207,000 USDC. This means a price of 1 NAU =
                0.00276 USDC
              </li>
              <li>NAUY/USDC: 10M NAUY + 13,800 USDC</li>
              <li>NAUN/USDC: 10M NAUN + 13,800 USDC</li>
            </ul>
            <Paragraph>
              Enables smooth price discovery and strong base/opinion token
              liquidity.
            </Paragraph>
          </div>

          <div>
            <h4 className="text-blue-900 font-semibold mb-2">
              5. Dev Team Vesting
            </h4>
            <ul className="list-disc list-inside text-sm text-gray-800">
              <li>200M NAU locked via vesting smart contract.</li>
              <li>1-year cliff + linear vesting release.</li>
            </ul>
            <Paragraph>
              Structured for alignment and long-term contribution commitment.
            </Paragraph>
          </div>

          <div>
            <h4 className="text-blue-900 font-semibold mb-2">
              6. Project Reserve
            </h4>
            <ul className="list-disc list-inside text-sm text-gray-800">
              <li>
                250M NAU reserved for partnerships, growth, and ecosystem
                initiatives.
              </li>
            </ul>
            <Paragraph>
              Supports sustainability without compromising circulating supply.
            </Paragraph>
          </div>
        </div>
      </SectionCard>
    </div>

    {/* Table + Conclusion */}
    <div className="max-w-6xl mx-auto space-y-6">
      <SectionCard title="Token Allocation Summary">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-800 border border-gray-300 rounded-lg overflow-hidden">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-4 py-2 font-semibold">Use</th>
                <th className="px-4 py-2 font-semibold">NAU</th>
                <th className="px-4 py-2 font-semibold">NAUY</th>
                <th className="px-4 py-2 font-semibold">NAUN</th>
                <th className="px-4 py-2 font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  use: "Total Supply",
                  nau: "1,000,000,000",
                  nauy: "15,000,000",
                  naun: "15,000,000",
                  notes: "Fixed cap",
                },
                {
                  use: "Launchpad",
                  nau: "125,000,000",
                  nauy: "",
                  naun: "",
                  notes: "0.0024 USDC/NAU",
                },
                {
                  use: "Staking Incentives",
                  nau: "350,000,000",
                  nauy: "5,000,000",
                  naun: "5,000,000",
                  notes: "3 pools: NAU, NAUY, NAUN",
                },
                {
                  use: "Liquidity",
                  nau: "75,000,000",
                  nauy: "10,000,000",
                  naun: "10,000,000",
                  notes: "Uniswap V3 pairs",
                },
                {
                  use: "Dev Vesting",
                  nau: "200,000,000",
                  nauy: "",
                  naun: "",
                  notes: "1yr cliff + vesting",
                },
                {
                  use: "Project Reserve",
                  nau: "250,000,000",
                  nauy: "",
                  naun: "",
                  notes: "Growth + ecosystem",
                },
              ].map(({ use, nau, nauy, naun, notes }) => (
                <tr className="border-t even:bg-gray-50" key={use}>
                  <td className="px-4 py-2">{use}</td>
                  <td className="px-4 py-2">{nau}</td>
                  <td className="px-4 py-2">{nauy}</td>
                  <td className="px-4 py-2">{naun}</td>
                  <td className="px-4 py-2">{notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <Paragraph className="text-gray-700 max-w-6xl mx-auto text-base sm:text-lg md:text-lg text-justify">
        UcWaves tokenomics reinforce fairness, decentralization, and long-term
        utility. With capped supply, burn-mint staking, and robust liquidity
        design, we’re building a sustainable value network.
      </Paragraph>
    </div>
  </div>
);

export default Tokenomics;
